import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import {
  BookOpen, Trophy, Star, Gamepad2, AlertTriangle, Activity,
} from "lucide-react";
import { differenceInYears, differenceInMonths, parseISO } from "date-fns";
import { motion } from "framer-motion";

interface Props {
  children: any[];
  selectedChild: string | null;
  onSelectChild: (id: string) => void;
  todayAttendance: any[];
  examResults: any[];
  gameResults: any[];
  childObservations: Record<string, { notes: string | null; sentiment: string } | undefined>;
  childActivityProgress: Record<string, number>;
}

const formatAge = (dob: string | null) => {
  if (!dob) return "-";
  const birth = parseISO(dob);
  const years = differenceInYears(new Date(), birth);
  if (years >= 1) return `${years} سنة`;
  return `${differenceInMonths(new Date(), birth)} شهر`;
};

export default function ParentChildCards({
  children,
  selectedChild,
  onSelectChild,
  todayAttendance,
  examResults,
  gameResults,
  childObservations,
  childActivityProgress,
}: Props) {
  const navigate = useNavigate();

  const getChildLastResult = (childId: string) => {
    const lastExam = examResults.find((e) => e.child_id === childId && e.percentage != null);
    const lastGame = gameResults.find((g) => g.child_id === childId);
    if (lastExam && lastGame) {
      return new Date(lastExam.created_at) > new Date(lastGame.created_at)
        ? { type: "exam", score: Math.round(lastExam.percentage) }
        : { type: "game", score: Math.round(lastGame.percentage || 0), stars: lastGame.stars };
    }
    if (lastExam) return { type: "exam", score: Math.round(lastExam.percentage) };
    if (lastGame) return { type: "game", score: Math.round(lastGame.percentage || 0), stars: lastGame.stars };
    return null;
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {children.map((child, i) => {
        const attended = todayAttendance.some((a) => a.attendee_id === child.id);
        const lastResult = getChildLastResult(child.id);
        const observation = childObservations[child.id];
        const activityPct = childActivityProgress[child.id] || 0;

        return (
          <motion.div key={child.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
            <Card
              className={`shadow-card cursor-pointer transition-all hover:shadow-md ${selectedChild === child.id ? "ring-2 ring-primary" : ""}`}
              onClick={() => onSelectChild(child.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={child.photo_url || ""} />
                      <AvatarFallback className="bg-primary/10 text-primary font-bold">{child.full_name?.charAt(0)}</AvatarFallback>
                    </Avatar>
                    <div className={`absolute -top-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-card ${attended ? "bg-emerald-500" : "bg-muted-foreground/30"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground truncate">{child.full_name}</h3>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-0.5">
                      <span>{formatAge(child.date_of_birth)}</span>
                      {child.classes && (
                        <>
                          <span>•</span>
                          <Badge variant="outline" className="text-xs px-1.5 py-0">{child.classes.name}</Badge>
                        </>
                      )}
                    </div>
                    {lastResult && (
                      <div className="flex items-center gap-1.5 mt-1">
                        {lastResult.type === "exam" ? (
                          <Trophy className="w-3 h-3 text-primary" />
                        ) : (
                          <Gamepad2 className="w-3 h-3 text-primary" />
                        )}
                        <span className={`text-xs font-medium ${lastResult.score >= 70 ? "text-emerald-600" : lastResult.score >= 50 ? "text-amber-600" : "text-destructive"}`}>
                          آخر نتيجة: {lastResult.score}%
                        </span>
                        {lastResult.type === "game" && lastResult.stars ? (
                          <div className="flex items-center gap-0">
                            {Array.from({ length: Math.min(lastResult.stars, 3) }).map((_, si) => (
                              <Star key={si} className="w-3 h-3 fill-amber-400 text-amber-400" />
                            ))}
                          </div>
                        ) : null}
                      </div>
                    )}
                    {child.health_notes && (
                      <div className="flex items-center gap-1 mt-1">
                        <AlertTriangle className="w-3 h-3 text-amber-500" />
                        <span className="text-xs text-amber-600">ملاحظات صحية</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Activity progress bar */}
                {activityPct > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
                      <span className="flex items-center gap-1"><Activity className="w-3 h-3" /> تقدم الأنشطة</span>
                      <span>{Math.round(activityPct)}%</span>
                    </div>
                    <Progress value={activityPct} className="h-1.5" />
                  </div>
                )}

                {/* Teacher observation */}
                {observation && (
                  <div className="mt-2 text-xs text-muted-foreground bg-muted/50 rounded px-2 py-1 line-clamp-1">
                    📝 {observation.notes || "ملاحظة من المعلم"}
                  </div>
                )}

                <div className="flex gap-2 mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-1.5 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/child/${child.id}`);
                    }}
                  >
                    <BookOpen className="w-3.5 h-3.5" />
                    الدروس والواجبات
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 gap-1.5 text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/dashboard/student/${child.id}`);
                    }}
                  >
                    <Activity className="w-3.5 h-3.5" />
                    الأنشطة التكميلية
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        );
      })}
    </div>
  );
}
